/**
 * Procedural sound patches (GF3).
 *
 * Every game sound is synthesized in Web Audio at play time — the audio
 * equivalent of the procedural sprite circles. No files, no loading, no
 * loop seams, and authored audio can replace any patch later without
 * touching the call sites (see specs/game-feel.md §Audio sourcing).
 *
 * Each patch is a function (ctx, destination, t0) that builds a short
 * one-shot node graph starting at time t0 and lets it garbage-collect
 * when the envelope closes. Ambient moods build a persistent graph and
 * return a stop() that ramps out.
 */

export type SoundId =
  | "step"
  | "turn"
  | "door_open"
  | "door_locked"
  | "attack_hit"
  | "attack_miss"
  | "enemy_died"
  | "player_hurt"
  | "spell_cast"
  | "trap_fire"
  | "trap_disarmed"
  | "camp_rest"
  | "level_up"
  | "loot_drop"
  | "heal"
  | "growl";

export type AmbientMood = "dungeon" | "wind" | "station" | "fold";

type Patch = (ctx: AudioContext, out: AudioNode, t0: number) => void;

// ── Shared helpers ───────────────────────────────────────────────────────────

/** One shared 2s white-noise buffer per context. */
const noiseBuffers = new WeakMap<AudioContext, AudioBuffer>();
function noiseBuffer(ctx: AudioContext): AudioBuffer {
  let buf = noiseBuffers.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buf.getChannelData(0);
    // Deterministic pseudo-noise (xorshift) — keeps the engine's no-RNG
    // hygiene and makes patches sound identical across sessions.
    let s = 0x9e3779b9;
    for (let i = 0; i < data.length; i++) {
      s ^= s << 13;
      s ^= s >>> 17;
      s ^= s << 5;
      data[i] = ((s >>> 0) / 0xffffffff) * 2 - 1;
    }
    noiseBuffers.set(ctx, buf);
  }
  return buf;
}

function envGain(ctx: AudioContext, out: AudioNode, t0: number, peak: number, attack: number, decay: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  g.connect(out);
  return g;
}

function noiseBurst(
  ctx: AudioContext,
  out: AudioNode,
  t0: number,
  opts: { peak: number; attack: number; decay: number; filterType: BiquadFilterType; freq: number; q?: number; rate?: number },
): void {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.playbackRate.value = opts.rate ?? 1;
  const f = ctx.createBiquadFilter();
  f.type = opts.filterType;
  f.frequency.value = opts.freq;
  f.Q.value = opts.q ?? 0.8;
  const g = envGain(ctx, out, t0, opts.peak, opts.attack, opts.decay);
  src.connect(f).connect(g);
  src.start(t0);
  src.stop(t0 + opts.attack + opts.decay + 0.05);
}

function tone(
  ctx: AudioContext,
  out: AudioNode,
  t0: number,
  opts: { type: OscillatorType; from: number; to?: number; peak: number; attack: number; decay: number; glide?: number },
): void {
  const o = ctx.createOscillator();
  o.type = opts.type;
  o.frequency.setValueAtTime(opts.from, t0);
  if (opts.to !== undefined) {
    o.frequency.exponentialRampToValueAtTime(Math.max(1, opts.to), t0 + (opts.glide ?? opts.attack + opts.decay));
  }
  const g = envGain(ctx, out, t0, opts.peak, opts.attack, opts.decay);
  o.connect(g);
  o.start(t0);
  o.stop(t0 + opts.attack + opts.decay + 0.05);
}

// ── One-shot patches ─────────────────────────────────────────────────────────

/** Step variant alternator — two slightly different scuffs read as gait. */
let stepParity = false;

export const PATCHES: Record<SoundId, Patch> = {
  step: (ctx, out, t0) => {
    stepParity = !stepParity;
    noiseBurst(ctx, out, t0, {
      peak: 0.22,
      attack: 0.004,
      decay: stepParity ? 0.085 : 0.075,
      filterType: "lowpass",
      freq: stepParity ? 340 : 300,
      rate: stepParity ? 0.9 : 1.0,
    });
    tone(ctx, out, t0, { type: "sine", from: stepParity ? 88 : 78, to: 45, peak: 0.1, attack: 0.004, decay: 0.06 });
  },

  turn: (ctx, out, t0) => {
    noiseBurst(ctx, out, t0, { peak: 0.05, attack: 0.01, decay: 0.07, filterType: "bandpass", freq: 900, q: 0.6 });
  },

  door_open: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "sine", from: 130, to: 55, peak: 0.3, attack: 0.006, decay: 0.28 });
    noiseBurst(ctx, out, t0 + 0.03, { peak: 0.12, attack: 0.02, decay: 0.3, filterType: "lowpass", freq: 500, rate: 0.55 });
  },

  door_locked: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "square", from: 220, peak: 0.1, attack: 0.004, decay: 0.05 });
    tone(ctx, out, t0 + 0.09, { type: "square", from: 185, peak: 0.1, attack: 0.004, decay: 0.08 });
  },

  attack_hit: (ctx, out, t0) => {
    noiseBurst(ctx, out, t0, { peak: 0.3, attack: 0.003, decay: 0.1, filterType: "bandpass", freq: 1400, q: 0.7 });
    tone(ctx, out, t0, { type: "triangle", from: 160, to: 70, peak: 0.24, attack: 0.003, decay: 0.11 });
  },

  attack_miss: (ctx, out, t0) => {
    const f = ctx.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.setValueAtTime(500, t0);
    f.frequency.exponentialRampToValueAtTime(2400, t0 + 0.14);
    f.Q.value = 2.5;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    const g = envGain(ctx, out, t0, 0.14, 0.02, 0.13);
    src.connect(f).connect(g);
    src.start(t0);
    src.stop(t0 + 0.2);
  },

  enemy_died: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "sawtooth", from: 220, to: 40, peak: 0.16, attack: 0.01, decay: 0.4, glide: 0.38 });
    noiseBurst(ctx, out, t0 + 0.05, { peak: 0.1, attack: 0.02, decay: 0.3, filterType: "lowpass", freq: 350, rate: 0.7 });
  },

  player_hurt: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "square", from: 300, to: 140, peak: 0.16, attack: 0.004, decay: 0.16 });
    noiseBurst(ctx, out, t0, { peak: 0.14, attack: 0.004, decay: 0.12, filterType: "highpass", freq: 900 });
  },

  spell_cast: (ctx, out, t0) => {
    for (const [i, from] of [340, 452, 570].entries()) {
      tone(ctx, out, t0 + i * 0.045, { type: "sine", from, to: from * 2.1, peak: 0.09, attack: 0.02, decay: 0.22, glide: 0.22 });
    }
  },

  trap_fire: (ctx, out, t0) => {
    noiseBurst(ctx, out, t0, { peak: 0.34, attack: 0.002, decay: 0.16, filterType: "highpass", freq: 500 });
    tone(ctx, out, t0, { type: "sawtooth", from: 130, to: 50, peak: 0.22, attack: 0.003, decay: 0.22 });
  },

  trap_disarmed: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "square", from: 660, peak: 0.07, attack: 0.004, decay: 0.05 });
    tone(ctx, out, t0 + 0.08, { type: "square", from: 880, peak: 0.07, attack: 0.004, decay: 0.09 });
  },

  camp_rest: (ctx, out, t0) => {
    for (const [i, from] of [196, 247, 294, 392].entries()) {
      tone(ctx, out, t0 + i * 0.12, { type: "triangle", from, peak: 0.08, attack: 0.05, decay: 0.7 });
    }
  },

  level_up: (ctx, out, t0) => {
    for (const [i, from] of [392, 494, 587, 784].entries()) {
      tone(ctx, out, t0 + i * 0.08, { type: "triangle", from, peak: 0.1, attack: 0.01, decay: 0.3 });
    }
  },

  loot_drop: (ctx, out, t0) => {
    tone(ctx, out, t0, { type: "sine", from: 990, to: 660, peak: 0.08, attack: 0.003, decay: 0.09 });
    tone(ctx, out, t0 + 0.06, { type: "sine", from: 1320, to: 880, peak: 0.06, attack: 0.003, decay: 0.12 });
  },

  heal: (ctx, out, t0) => {
    // Soft rising third — restorative, quieter than the camp-rest chord.
    tone(ctx, out, t0, { type: "triangle", from: 523, peak: 0.07, attack: 0.02, decay: 0.28 });
    tone(ctx, out, t0 + 0.09, { type: "triangle", from: 659, peak: 0.07, attack: 0.02, decay: 0.34 });
  },

  growl: (ctx, out, t0) => {
    const o = ctx.createOscillator();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(72, t0);
    o.frequency.linearRampToValueAtTime(58, t0 + 0.7);
    const wob = ctx.createOscillator();
    wob.frequency.value = 11;
    const wobGain = ctx.createGain();
    wobGain.gain.value = 14;
    wob.connect(wobGain).connect(o.frequency);
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 320;
    const g = envGain(ctx, out, t0, 0.16, 0.09, 0.65);
    o.connect(f).connect(g);
    o.start(t0);
    wob.start(t0);
    o.stop(t0 + 0.85);
    wob.stop(t0 + 0.85);
  },
};

// ── Ambient moods ────────────────────────────────────────────────────────────

export interface AmbientHandle {
  /** Ramp out over `fadeS` seconds and tear the graph down. */
  stop(fadeS: number): void;
}

/**
 * Continuous ambient bed for a mood. Synthesis is endless — no loop
 * points, no seams. Levels are deliberately low; ambience should be felt
 * more than heard.
 */
export function startAmbientPatch(ctx: AudioContext, out: AudioNode, mood: AmbientMood): AmbientHandle {
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, ctx.currentTime);
  master.gain.linearRampToValueAtTime(1, ctx.currentTime + 2.5);
  master.connect(out);
  const stops: Array<() => void> = [];

  const addTone = (type: OscillatorType, freq: number, gain: number, lfoHz?: number, lfoDepth?: number) => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = gain;
    o.connect(g).connect(master);
    o.start();
    stops.push(() => o.stop());
    if (lfoHz && lfoDepth) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = lfoHz;
      const lg = ctx.createGain();
      lg.gain.value = lfoDepth;
      lfo.connect(lg).connect(o.frequency);
      lfo.start();
      stops.push(() => lfo.stop());
    }
  };

  const addNoise = (filterType: BiquadFilterType, freq: number, q: number, gain: number, lfoHz?: number, lfoDepth?: number) => {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    src.loop = true;
    const f = ctx.createBiquadFilter();
    f.type = filterType;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(f).connect(g).connect(master);
    src.start();
    stops.push(() => src.stop());
    if (lfoHz && lfoDepth) {
      const lfo = ctx.createOscillator();
      lfo.frequency.value = lfoHz;
      const lg = ctx.createGain();
      lg.gain.value = lfoDepth;
      lfo.connect(lg).connect(g.gain);
      lfo.start();
      stops.push(() => lfo.stop());
    }
  };

  switch (mood) {
    case "dungeon":
      addTone("sine", 55, 0.045, 0.06, 1.5);
      addTone("sine", 82.6, 0.028, 0.043, 2.2);
      addNoise("lowpass", 210, 0.5, 0.02, 0.05, 0.011);
      break;
    case "wind":
      addNoise("bandpass", 420, 0.45, 0.05, 0.07, 0.03);
      addNoise("bandpass", 950, 0.8, 0.018, 0.11, 0.012);
      addTone("sine", 68, 0.012);
      break;
    case "station":
      addTone("sawtooth", 60, 0.018);
      addTone("sine", 120, 0.026, 0.031, 3);
      addTone("sine", 3960, 0.0035, 0.017, 240);
      addNoise("lowpass", 300, 0.4, 0.012);
      break;
    case "fold":
      addTone("sine", 110, 0.03, 0.021, 4.6);
      addTone("sine", 111.7, 0.03, 0.013, 3.8);
      addTone("sine", 55.4, 0.024, 0.008, 2.1);
      addNoise("highpass", 5200, 0.7, 0.0028, 0.023, 0.002);
      break;
  }

  return {
    stop(fadeS: number): void {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(master.gain.value, t);
      master.gain.linearRampToValueAtTime(0.0001, t + fadeS);
      setTimeout(() => {
        for (const s of stops) s();
        master.disconnect();
      }, fadeS * 1000 + 100);
    },
  };
}
